import React, { useState } from 'react';
import Modal from './Modal';
import Select from 'react-select';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import axiosInstance from '../utility/axiosInstance';
import { useToast } from './ToastContext';
import { getSelectStyles } from '../utility/selectStyles';

interface RollingUpdateModalProps {
    isOpen: boolean;
    onClose: () => void;
    onStarted: () => void;
    cluster: any;
}

const RollingUpdateModal: React.FC<RollingUpdateModalProps> = ({
    isOpen, onClose, onStarted, cluster,
}) => {
    const { showToast } = useToast();
    const snapshots = useSelector((state: RootState) => state.snapshots.snapshots);
    const [selectedSnapshot, setSelectedSnapshot] = useState<any>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Only show snapshots that look like base images (not cirros test images)
    const snapshotOptions = snapshots.map(s => ({
        value: s.id,
        label: s.name,
    }));

    const handleStart = async () => {
        if (!selectedSnapshot) {
            showToast('Please select a snapshot', 'error');
            return;
        }
        setIsSubmitting(true);
        try {
            // Trigger createSnapshotWorkflow on the selected snapshot's source instance
            // with updateCluster=true to roll out to the Heat autoscaling group
            await axiosInstance.post('/v1/instances/snapshot', {
                instanceId: selectedSnapshot.value,  // snapshot id used as image id
                snapshotName: `rolling-update-${Date.now()}`,
                updateCluster: true,
                stackId: cluster.id,
                stackName: cluster.stack_name,
                metadata: { base_image_name: selectedSnapshot.label },
            });
            showToast('Rolling update started - real-time progress will appear in notifications', 'success');
            onStarted();
            onClose();
        } catch (err: any) {
            showToast(err.response?.data?.error || 'Failed to start rolling update', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={`Rolling Update: ${cluster?.stack_name}`} size="md">
            <div className="create-form">
                <div style={{
                    background: 'var(--surface-alt)',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    marginBottom: '20px',
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary)',
                    lineHeight: 1.6,
                }}>
                    <strong style={{ color: 'var(--text-primary)' }}>How rolling updates work:</strong>
                    <ol style={{ margin: '8px 0 0 16px', padding: 0 }}>
                        <li>Select a snapshot to use as the new base image</li>
                        <li>The cluster autoscaling group is updated with the new image</li>
                        <li>New instances spawned by autoscaling will use the new image</li>
                        <li>Existing instances are replaced gradually as the group scales</li>
                    </ol>
                </div>

                <div className="form-group">
                    <label>Select snapshot to deploy</label>
                    <Select
                        options={snapshotOptions}
                        value={selectedSnapshot}
                        onChange={setSelectedSnapshot}
                        placeholder={snapshotOptions.length === 0 ? 'No snapshots available - create one first' : 'Select snapshot...'}
                        isSearchable
                        isDisabled={snapshotOptions.length === 0}
                        styles={getSelectStyles()}
                        noOptionsMessage={() => 'No snapshots yet'}
                    />
                    {snapshotOptions.length === 0 && (
                        <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '6px' }}>
                            Create a snapshot from one of your instances first (Instances → Actions → Create Snapshot).
                        </p>
                    )}
                </div>

                <div className="modal-actions">
                    <button
                        type="button"
                        className="cancel-button"
                        onClick={onClose}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="submit-button"
                        onClick={handleStart}
                        disabled={isSubmitting || !selectedSnapshot}
                    >
                        {isSubmitting ? 'Starting…' : 'Start Rolling Update'}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default RollingUpdateModal;
